import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  CheckCircle2,
  Phone,
  Mail,
  Calendar,
  DollarSign,
  FileText,
  TrendingUp,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import type { PromiseToPay } from '@/types/collections';
import { formatCurrency } from '@/lib/utils/formatters';

interface CompleteActionModalProps {
  promise: PromiseToPay;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onActionComplete: (actionData: ActionData) => void;
}

export interface ActionData {
  actionType: string;
  outcome: string;
  notes: string;
  followUpDate?: Date;
  newPromiseDate?: Date;
  paymentAmount?: number;
  newStatus?: PromiseToPay['status'];
}

export const CompleteActionModal: React.FC<CompleteActionModalProps> = ({
  promise,
  open,
  onOpenChange,
  onActionComplete,
}) => {
  const [selectedAction, setSelectedAction] = useState<string>('');
  const [outcome, setOutcome] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [followUpDate, setFollowUpDate] = useState<string>('');
  const [newPromiseDate, setNewPromiseDate] = useState<string>('');
  const [paymentAmount, setPaymentAmount] = useState<string>('');
  const [newStatus, setNewStatus] = useState<PromiseToPay['status']>(promise.status);

  // Determine available actions based on promise status
  const getAvailableActions = () => {
    const today = new Date();
    const promiseDate = new Date(promise.promiseDate);
    const daysUntilPromise = Math.ceil((promiseDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

    const actions: Array<{
      id: string;
      label: string;
      description: string;
      icon: React.ReactNode;
      color: string;
    }> = [];

    switch (promise.status) {
      case 'Active':
        if (daysUntilPromise < 0) {
          // Promise date has passed
          actions.push(
            {
              id: 'verify_payment',
              label: 'Verify Payment Received',
              description: 'Confirm that payment has been received',
              icon: <CheckCircle2 className="h-4 w-4" />,
              color: 'text-green-600',
            },
            {
              id: 'mark_broken',
              label: 'Mark Promise as Broken',
              description: 'Customer did not fulfill the payment promise',
              icon: <XCircle className="h-4 w-4" />,
              color: 'text-red-600',
            },
            {
              id: 'partial_payment',
              label: 'Record Partial Payment',
              description: 'Customer made a partial payment',
              icon: <DollarSign className="h-4 w-4" />,
              color: 'text-amber-600',
            },
            {
              id: 'contact_customer',
              label: 'Contact Customer',
              description: 'Call or email customer for follow-up',
              icon: <Phone className="h-4 w-4" />,
              color: 'text-blue-600',
            }
          );
        } else if (daysUntilPromise <= 7) {
          // Promise date is within a week
          actions.push(
            {
              id: 'send_reminder',
              label: 'Send Payment Reminder',
              description: 'Email or SMS reminder about upcoming payment',
              icon: <Mail className="h-4 w-4" />,
              color: 'text-blue-600',
            },
            {
              id: 'schedule_call',
              label: 'Schedule Follow-up Call',
              description: 'Set up a call to confirm payment commitment',
              icon: <Phone className="h-4 w-4" />,
              color: 'text-purple-600',
            },
            {
              id: 'verify_payment',
              label: 'Verify Early Payment',
              description: 'Customer paid before the promise date',
              icon: <CheckCircle2 className="h-4 w-4" />,
              color: 'text-green-600',
            }
          );
        } else {
          // Promise date is more than a week away
          actions.push(
            {
              id: 'add_note',
              label: 'Add Monitoring Note',
              description: 'Record observation or update',
              icon: <FileText className="h-4 w-4" />,
              color: 'text-gray-600',
            },
            {
              id: 'schedule_call',
              label: 'Schedule Follow-up Call',
              description: 'Set up a future call to confirm commitment',
              icon: <Phone className="h-4 w-4" />,
              color: 'text-purple-600',
            }
          );
        }
        break;

      case 'Broken':
        actions.push(
          {
            id: 'contact_customer',
            label: 'Contact Customer',
            description: 'Reach out to understand the situation',
            icon: <Phone className="h-4 w-4" />,
            color: 'text-blue-600',
          },
          {
            id: 'negotiate_new',
            label: 'Negotiate New Promise',
            description: 'Work out a new payment arrangement',
            icon: <Calendar className="h-4 w-4" />,
            color: 'text-purple-600',
          },
          {
            id: 'escalate',
            label: 'Escalate Case',
            description: 'Move to next collection phase',
            icon: <TrendingUp className="h-4 w-4" />,
            color: 'text-red-600',
          },
          {
            id: 'verify_payment',
            label: 'Verify Late Payment',
            description: 'Customer paid after promise date',
            icon: <CheckCircle2 className="h-4 w-4" />,
            color: 'text-green-600',
          }
        );
        break;

      case 'Partially Paid':
        actions.push(
          {
            id: 'verify_full_payment',
            label: 'Verify Full Payment',
            description: 'Remaining balance has been paid',
            icon: <CheckCircle2 className="h-4 w-4" />,
            color: 'text-green-600',
          },
          {
            id: 'contact_customer',
            label: 'Follow Up on Balance',
            description: 'Contact customer about remaining amount',
            icon: <Phone className="h-4 w-4" />,
            color: 'text-blue-600',
          },
          {
            id: 'negotiate_new',
            label: 'Negotiate New Promise',
            description: 'Set up new promise for remaining balance',
            icon: <Calendar className="h-4 w-4" />,
            color: 'text-purple-600',
          }
        );
        break;

      case 'Paid':
        actions.push({
          id: 'add_note',
          label: 'Add Completion Note',
          description: 'Record final notes about this promise',
          icon: <FileText className="h-4 w-4" />,
          color: 'text-gray-600',
        });
        break;
    }

    return actions;
  };

  const availableActions = getAvailableActions();

  // Get outcome options based on selected action
  const getOutcomeOptions = () => {
    switch (selectedAction) {
      case 'verify_payment':
      case 'verify_full_payment':
        return [
          { value: 'payment_confirmed', label: 'Payment Confirmed - Full Amount' },
          { value: 'payment_pending', label: 'Payment Pending Verification' },
        ];
      case 'verify_early_payment':
        return [
          { value: 'early_payment_confirmed', label: 'Early Payment Confirmed' },
          { value: 'payment_pending', label: 'Payment Pending Verification' },
        ];
      case 'mark_broken':
        return [
          { value: 'no_payment', label: 'No Payment Received' },
          { value: 'insufficient_funds', label: 'Customer Cited Insufficient Funds' },
          { value: 'dispute_raised', label: 'Customer Raised Dispute' },
          { value: 'no_response', label: 'No Response from Customer' },
        ];
      case 'partial_payment':
        return [
          { value: 'partial_received', label: 'Partial Payment Received' },
          { value: 'payment_plan_started', label: 'Payment Plan Initiated' },
        ];
      case 'contact_customer':
        return [
          { value: 'spoke_with_customer', label: 'Spoke with Customer' },
          { value: 'left_voicemail', label: 'Left Voicemail' },
          { value: 'no_answer', label: 'No Answer' },
          { value: 'email_sent', label: 'Email Sent' },
        ];
      case 'send_reminder':
        return [
          { value: 'reminder_sent', label: 'Reminder Sent Successfully' },
          { value: 'delivery_failed', label: 'Delivery Failed' },
        ];
      case 'schedule_call':
        return [
          { value: 'call_scheduled', label: 'Follow-up Call Scheduled' },
          { value: 'customer_unavailable', label: 'Customer Unavailable' },
        ];
      case 'negotiate_new':
        return [
          { value: 'new_promise_agreed', label: 'New Promise Agreed' },
          { value: 'negotiation_ongoing', label: 'Negotiation Ongoing' },
          { value: 'customer_refused', label: 'Customer Refused' },
        ];
      case 'escalate':
        return [
          { value: 'escalated_to_manager', label: 'Escalated to Manager' },
          { value: 'escalated_to_legal', label: 'Escalated to Legal' },
          { value: 'sent_to_collections', label: 'Sent to External Collections' },
        ];
      case 'add_note':
        return [
          { value: 'note_added', label: 'Note Added' },
          { value: 'observation_recorded', label: 'Observation Recorded' },
        ];
      default:
        return [];
    }
  };

  const outcomeOptions = getOutcomeOptions();

  // Determine if additional fields are needed
  const needsFollowUpDate = ['schedule_call', 'contact_customer'].includes(selectedAction);
  const needsNewPromiseDate = selectedAction === 'negotiate_new';
  const needsPaymentAmount = ['partial_payment', 'verify_payment', 'verify_full_payment'].includes(selectedAction);
  const needsStatusChange = ['verify_payment', 'verify_full_payment', 'mark_broken', 'partial_payment'].includes(
    selectedAction
  );

  // Handle form submission
  const handleSubmit = () => {
    // Validation
    if (!selectedAction) {
      toast.error('Please select an action');
      return;
    }

    if (!outcome) {
      toast.error('Please select an outcome');
      return;
    }

    if (!notes.trim()) {
      toast.error('Please add notes about this action');
      return;
    }

    if (needsFollowUpDate && !followUpDate) {
      toast.error('Please select a follow-up date');
      return;
    }

    if (needsNewPromiseDate && !newPromiseDate) {
      toast.error('Please select a new promise date');
      return;
    }

    if (needsPaymentAmount && (!paymentAmount || parseFloat(paymentAmount) <= 0)) {
      toast.error('Please enter a valid payment amount');
      return;
    }

    // Prepare action data
    const actionData: ActionData = {
      actionType: selectedAction,
      outcome,
      notes,
      ...(needsFollowUpDate && followUpDate && { followUpDate: new Date(followUpDate) }),
      ...(needsNewPromiseDate && newPromiseDate && { newPromiseDate: new Date(newPromiseDate) }),
      ...(needsPaymentAmount && paymentAmount && { paymentAmount: parseFloat(paymentAmount) }),
      ...(needsStatusChange && { newStatus }),
    };

    // Call the callback
    onActionComplete(actionData);

    // Reset form
    setSelectedAction('');
    setOutcome('');
    setNotes('');
    setFollowUpDate('');
    setNewPromiseDate('');
    setPaymentAmount('');
    setNewStatus(promise.status);

    // Close modal
    onOpenChange(false);

    // Show success message
    toast.success('Action completed successfully');
  };

  const handleCancel = () => {
    setSelectedAction('');
    setOutcome('');
    setNotes('');
    setFollowUpDate('');
    setNewPromiseDate('');
    setPaymentAmount('');
    setNewStatus(promise.status);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl flex items-center gap-2">
            <CheckCircle2 className="h-6 w-6 text-primary" />
            Complete Action
          </DialogTitle>
          <DialogDescription>
            Select and complete an action for promise <span className="font-mono font-semibold">{promise.id}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Promise Summary */}
          <div className="bg-muted/50 rounded-lg p-4 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-muted-foreground">Customer</span>
              <span className="text-sm font-semibold">{promise.customerName}</span>
            </div>
            <Separator />
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-muted-foreground">Promise Amount</span>
              <span className="text-sm font-semibold">{formatCurrency(promise.promisedAmount)}</span>
            </div>
            <Separator />
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-muted-foreground">Current Status</span>
              <Badge
                className={
                  promise.status === 'Active'
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                    : promise.status === 'Broken'
                      ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                      : promise.status === 'Paid'
                        ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                        : 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400'
                }
              >
                {promise.status}
              </Badge>
            </div>
          </div>

          {/* Action Selection */}
          <div className="space-y-3">
            <Label className="text-base font-semibold">Select Action</Label>
            <RadioGroup value={selectedAction} onValueChange={setSelectedAction}>
              <div className="space-y-2">
                {availableActions.map((action) => (
                  <div
                    key={action.id}
                    className={`flex items-start space-x-3 rounded-lg border p-4 cursor-pointer transition-all hover:bg-muted/50 ${
                      selectedAction === action.id ? 'border-primary bg-primary/5 ring-2 ring-primary/20' : ''
                    }`}
                    onClick={() => setSelectedAction(action.id)}
                  >
                    <RadioGroupItem value={action.id} id={action.id} className="mt-1" />
                    <div className="flex-1 space-y-1">
                      <div className="flex items-center gap-2">
                        <span className={action.color}>{action.icon}</span>
                        <Label htmlFor={action.id} className="font-medium cursor-pointer">
                          {action.label}
                        </Label>
                      </div>
                      <p className="text-sm text-muted-foreground">{action.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </RadioGroup>
          </div>

          {/* Outcome Selection */}
          {selectedAction && outcomeOptions.length > 0 && (
            <div className="space-y-2">
              <Label htmlFor="outcome" className="text-base font-semibold">
                Outcome
              </Label>
              <Select value={outcome} onValueChange={setOutcome}>
                <SelectTrigger id="outcome">
                  <SelectValue placeholder="Select outcome" />
                </SelectTrigger>
                <SelectContent>
                  {outcomeOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Additional Fields Based on Action */}
          {needsPaymentAmount && (
            <div className="space-y-2">
              <Label htmlFor="paymentAmount" className="text-base font-semibold">
                Payment Amount
              </Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                <Input
                  id="paymentAmount"
                  type="number"
                  placeholder="0.00"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  className="pl-7"
                  step="0.01"
                  min="0"
                />
              </div>
            </div>
          )}

          {needsNewPromiseDate && (
            <div className="space-y-2">
              <Label htmlFor="newPromiseDate" className="text-base font-semibold">
                New Promise Date
              </Label>
              <Input
                id="newPromiseDate"
                type="date"
                value={newPromiseDate}
                onChange={(e) => setNewPromiseDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
          )}

          {needsFollowUpDate && (
            <div className="space-y-2">
              <Label htmlFor="followUpDate" className="text-base font-semibold">
                Follow-up Date
              </Label>
              <Input
                id="followUpDate"
                type="date"
                value={followUpDate}
                onChange={(e) => setFollowUpDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
          )}

          {needsStatusChange && (
            <div className="space-y-2">
              <Label htmlFor="newStatus" className="text-base font-semibold">
                Update Promise Status
              </Label>
              <Select value={newStatus} onValueChange={(value) => setNewStatus(value as PromiseToPay['status'])}>
                <SelectTrigger id="newStatus">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Broken">Broken</SelectItem>
                  <SelectItem value="Paid">Paid</SelectItem>
                  <SelectItem value="Partially Paid">Partially Paid</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Notes */}
          {selectedAction && (
            <div className="space-y-2">
              <Label htmlFor="notes" className="text-base font-semibold">
                Notes <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="notes"
                placeholder="Add detailed notes about this action..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={4}
                className="resize-none"
              />
              <p className="text-xs text-muted-foreground">
                Provide context and details about the action taken and any relevant information.
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!selectedAction || !outcome || !notes.trim()}>
            <CheckCircle2 className="h-4 w-4 mr-2" />
            Complete Action
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
